// libraries
import type { IPencilOfferInputs, OfferType } from '@makemydeal/dr-platform-types';
import { OFFER_TYPE } from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';

// interfaces/types
import type { OfferSummary } from '../types/commonTypes';

// utils
import {
    getCurrentOfferType,
    getIncentiveTotalByOfferType,
    getOfferDetails,
    getTerm,
    getTradeInAmountOwed,
    getTradeInValue,
    getVehicleDetails,
    shouldIncludeTradeIn
} from './offerNodeSelectors';
import { getDueAtSigning } from './feesSelectors';

export function getOfferSummary(state: OfferReduxBaseState, offerType: OfferType): OfferSummary {
    let summary = {
        adjustedResidualValue: 0,
        amountFinanced: 0,
        annualMiles: 0,
        dealSummary: {},
        downPayment: 0,
        dueAtSigning: 0,
        isFinanceNegativeTradeIn: false,
        listedPrice: 0,
        monthlyPayment: 0,
        monthlyPaymentWithoutProducts: 0,
        monthlyPaymentRounded: 0,
        offerPrice: 0,
        initialOfferPrice: 0,
        offerType: '',
        residualPercentage: 0,
        retailPrice: 0,
        sellRate: 0,
        term: {
            months: 0,
            apr: 0
        },
        totalIncentives: 0,
        tradeInAmountOwed: 0,
        tradeInValue: 0,
        vehicleType: ''
    };

    const vehicle = getVehicleDetails(state);
    const listedPrice = vehicle.listedPrice || 0;
    const retailPrice = vehicle.retailPrice || 0;
    const vehicleType = vehicle.type || vehicle.condition || null;
    const offerDetails = getOfferDetails(state, offerType) || ({} as IPencilOfferInputs);
    const { annualMiles, downPayment, offerPrice, initialOfferPrice, term } = offerDetails;
    const termPayment: any = getTerm(state, term!) || {};
    const {
        adjustedResidualValue,
        apr,
        amountFinanced,
        months,
        monthlyPayment,
        monthlyPaymentWithoutProducts,
        monthlyPaymentRounded,
        dealSummary,
        residualPercentage,
        sellRate
    } = termPayment;
    const total = getIncentiveTotalByOfferType(state, offerType);
    const tradeInAmountOwed = getTradeInAmountOwed(state);
    const tradeInValue = getTradeInValue(state);
    const isFinanceNegativeTradeIn = tradeInValue < tradeInAmountOwed && shouldIncludeTradeIn(state);
    const dueAtSigning = getDueAtSigning(state);

    summary = {
        adjustedResidualValue,
        amountFinanced,
        annualMiles: annualMiles ?? 0,
        dealSummary,
        downPayment: downPayment ?? 0,
        dueAtSigning,
        isFinanceNegativeTradeIn,
        listedPrice: listedPrice,
        monthlyPayment,
        monthlyPaymentWithoutProducts,
        monthlyPaymentRounded,
        offerPrice: offerPrice ?? 0,
        initialOfferPrice: initialOfferPrice ?? 0,
        offerType,
        residualPercentage,
        retailPrice: retailPrice,
        sellRate,
        term: {
            months,
            apr
        },
        totalIncentives: total || 0,
        tradeInAmountOwed,
        tradeInValue,
        vehicleType: vehicleType as string
    };

    if (getCurrentOfferType(state) !== OFFER_TYPE.finance) {
        summary.term.apr = 0;
    }

    return summary;
}
